import jsPDF from 'jspdf';

/**
 * Exporte les données d'historique en PDF
 * @param {Array} data - Les données d'historique à exporter
 * @param {Object} filters - Les filtres appliqués (optionnel)
 * @param {string} organizationName - Nom de l'organisation
 */
export const exportHistoryToPDF = (data, filters = {}, organizationName = '') => {
  try {
    // Créer une nouvelle instance jsPDF
    const doc = new jsPDF();
    
    // Configuration des couleurs
    const primaryColor = [52, 58, 64]; // Gris foncé
    const secondaryColor = [108, 117, 125]; // Gris moyen

    // Variables pour la mise en page
    let yPosition = 20;
    const pageHeight = doc.internal.pageSize.height;
    const pageWidth = doc.internal.pageSize.width;
    const margin = 14;
    const lineHeight = 7;

    // Fonction pour ajouter une nouvelle page si nécessaire
    const checkPageBreak = (requiredSpace = 10) => {
      if (yPosition + requiredSpace > pageHeight - 30) {
        doc.addPage();
        yPosition = 20;
        return true;
      }
      return false;
    };

    // En-tête du document
    doc.setFontSize(20);
    doc.setTextColor(...primaryColor);
    doc.text('Historique des Actions', margin, yPosition);
    yPosition += 15;
    
    if (organizationName) {
      doc.setFontSize(12);
      doc.setTextColor(...secondaryColor);
      doc.text(`Organisation: ${organizationName}`, margin, yPosition);
      yPosition += 10;
    }

    // Date d'export
    const exportDate = new Date().toLocaleString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
    doc.setFontSize(10);
    doc.text(`Exporté le: ${exportDate}`, margin, yPosition);
    yPosition += 15;

    // Affichage des filtres appliqués
    const activeFilters = Object.entries(filters).filter(([key, value]) => value && value.trim() !== '');
    
    if (activeFilters.length > 0) {
      checkPageBreak(30);
      doc.setFontSize(10);
      doc.setTextColor(...secondaryColor);
      doc.text('Filtres appliqués:', margin, yPosition);
      yPosition += lineHeight;
      
      activeFilters.forEach(([key, value]) => {
        let filterLabel = '';
        switch (key) {
          case 'user_id':
            filterLabel = 'Utilisateur';
            break;
          case 'action_type':
            filterLabel = 'Type d\'action';
            break;
          case 'entity_type':
            filterLabel = 'Type d\'entité';
            break;
          case 'start_date':
            filterLabel = 'Date de début';
            break;
          case 'end_date':
            filterLabel = 'Date de fin';
            break;
          default:
            filterLabel = key;
        }
        checkPageBreak();
        doc.text(`• ${filterLabel}: ${value}`, margin + 4, yPosition);
        yPosition += 5;
      });
      yPosition += 10;
    }

    // Fonction pour formater la date
    const formatDate = (dateString) => {
      if (!dateString) return '-';
      try {
        return new Date(dateString).toLocaleString('fr-FR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return dateString;
      }
    };

    // Fonction pour traduire le type d'action
    const translateAction = (action) => {
      switch (action) {
        case 'CREATE':
          return 'Créer';
        case 'UPDATE':
          return 'Modifier';
        case 'DELETE':
          return 'Supprimer';
        case 'LOGIN':
          return 'Connexion';
        case 'LOGOUT':
          return 'Déconnexion';
        default:
          return action || 'Inconnue';
      }
    };

    // Fonction pour dessiner une ligne horizontale
    const drawLine = (y, startX = margin, endX = pageWidth - margin) => {
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.1);
      doc.line(startX, y, endX, y);
    };

    // Fonction pour tronquer le texte si trop long
    const truncateText = (text, maxWidth, fontSize = 8) => {
      text = String(text); // Ensure text is a string
      doc.setFontSize(fontSize);
      const textWidth = doc.getTextWidth(text);
      if (textWidth <= maxWidth) {
        return text;
      }
      
      // Tronquer le texte et ajouter "..."
      let truncated = text;
      while (doc.getTextWidth(truncated + '...') > maxWidth && truncated.length > 0) {
        truncated = truncated.slice(0, -1);
      }
      return truncated + '...';
    };

    // Configuration des colonnes
    const columns = [
      { header: 'Date', width: 32, x: margin },
      { header: 'Utilisateur', width: 25, x: margin + 32 },
      { header: 'Action', width: 20, x: margin + 57 },
      { header: 'Entité', width: 25, x: margin + 77 },
      { header: 'ID Entité', width: 22, x: margin + 102 },
      { header: 'Adresse IP', width: 30, x: margin + 124 }
    ];

    // Dessiner l'en-tête du tableau
    checkPageBreak(20);
    const headerY = yPosition;
    
    // Fond de l'en-tête
    doc.setFillColor(248, 249, 250);
    doc.rect(margin, headerY - 3, pageWidth - 2 * margin, 10, 'F');
    
    // Bordures de l'en-tête
    doc.setDrawColor(222, 226, 230);
    doc.setLineWidth(0.1);
    doc.rect(margin, headerY - 3, pageWidth - 2 * margin, 10);
    
    // Texte de l'en-tête
    doc.setFontSize(9);
    doc.setTextColor(...primaryColor);
    doc.setFont(undefined, 'bold');
    
    columns.forEach(col => {
      const verticalLines = col.x > margin;
      if (verticalLines) {
        doc.line(col.x, headerY - 3, col.x, headerY + 7);
      }
      doc.text(col.header, col.x + 2, headerY + 3);
    });
    
    yPosition = headerY + 10;
    doc.setFont(undefined, 'normal');

    // Dessiner les données
    data.forEach((item, index) => {
      checkPageBreak(8);
      
      const rowY = yPosition;
      
      // Fond alterné pour les lignes
      if (index % 2 === 1) {
        doc.setFillColor(248, 249, 250);
        doc.rect(margin, rowY - 2, pageWidth - 2 * margin, 7, 'F');
      }
      
      // Bordures de la ligne
      doc.setDrawColor(222, 226, 230);
      doc.setLineWidth(0.1);
      doc.rect(margin, rowY - 2, pageWidth - 2 * margin, 7);
      
      // Données de la ligne
      const rowData = [
        formatDate(item.created_at || item.date),
        item.name || 'system',
        translateAction(item.action_type || item.action),
        item.entity_type || item.entite || '-',
        item.entity_id || item.entite_id || '-',
        item.ip_address || item.adresse_ip || '-'
      ];
      
      doc.setFontSize(8);
      doc.setTextColor(...primaryColor);
      
      columns.forEach((col, colIndex) => {
        // Lignes verticales
        if (col.x > margin) {
          doc.line(col.x, rowY - 2, col.x, rowY + 5);
        }
        
        // Couleur spéciale pour les actions
        if (colIndex === 2) { // Colonne Action
          const actionValue = rowData[colIndex];
          switch (actionValue) {
            case 'Créer':
              doc.setTextColor(40, 167, 69);
              break;
            case 'Modifier':
              doc.setTextColor(255, 140, 0);
              break;
            case 'Supprimer':
              doc.setTextColor(220, 53, 69);
              break;
            case 'Connexion':
            case 'Déconnexion':
              doc.setTextColor(23, 162, 184);
              break;
            default:
              doc.setTextColor(...primaryColor);
          }
        }
        
        const text = truncateText(rowData[colIndex] || '', col.width - 4);
        doc.text(text, col.x + 2, rowY + 2);
        
        // Remettre la couleur par défaut
        doc.setTextColor(...primaryColor);
      });
      
      yPosition += 7;
    });

    // Ligne de fin du tableau
    drawLine(yPosition - 2);

    // Informations de résumé
    yPosition += 10;
    checkPageBreak(15);
    doc.setFontSize(10);
    doc.setTextColor(...secondaryColor);
    doc.text(`Total des entrées: ${data.length}`, margin, yPosition);

    // Pied de page avec numérotation
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(...secondaryColor);
      
      const pageFooterY = pageHeight - 10;
      doc.text(`Page ${i} sur ${pageCount}`, margin, pageFooterY);
      doc.text(`Généré le ${exportDate}`, pageWidth - 60, pageFooterY);
    }

    // Générer le nom du fichier
    const fileName = `historique_actions_${organizationName ? organizationName.replace(/[^a-zA-Z0-9]/g, '_') + '_' : ''}${new Date().toISOString().split('T')[0]}.pdf`;

    // Télécharger le PDF
    doc.save(fileName);

    return {
      success: true,
      message: 'Export PDF réussi',
      fileName
    };

  } catch (error) {
    console.error('Erreur lors de l\'export PDF:', error);
    return {
      success: false,
      message: 'Erreur lors de l\'export PDF: ' + error.message,
      error
    };
  }
};

/**
 * Exporte seulement les données visibles (page courante) en PDF
 * @param {Array} currentPageData - Les données de la page courante
 * @param {Object} paginationInfo - Informations de pagination
 * @param {Object} filters - Les filtres appliqués
 * @param {string} organizationName - Nom de l'organisation
 */
export const exportCurrentPageToPDF = (currentPageData, paginationInfo, filters = {}, organizationName = '') => {
  const result = exportHistoryToPDF(currentPageData, filters, organizationName);
  
  if (result.success) {
    // Modifier le nom du fichier pour indiquer qu'il s'agit d'une page
    const baseFileName = result.fileName.replace('.pdf', '');
    const pageFileName = `${baseFileName}_page_${paginationInfo.currentPage}.pdf`;
    result.fileName = pageFileName;
  }
  
  return result;
};